/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'

import { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Table as BTable } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Form from 'react-bootstrap/Form';
import { faCaretDown, faCaretUp, faPen, faTrash, faPersonRunning, faQ } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import Button from 'react-bootstrap/Button';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { deleteAiOp, AiOp, queueAiProbe, execControl } from '../data';
import { StatusContext, StatusContextType } from './StatusContext';

interface AiOpsTableProps {
    data: AiOp[];
    setReload: (value: boolean) => void;
    setRowSelection: (selection: Record<string, boolean>) => void;
    rowSelection: Record<string, boolean>;
}

function AiOpsTable({ data, setReload, setRowSelection, rowSelection }: AiOpsTableProps) {
    const navigate = useNavigate();
    const [globalFilter, setGlobalFilter] = useState<string>("");
    const statusContext = useContext<StatusContextType>(StatusContext);
    const theme: any = useTheme();

    const handleDelete = async (id: string, description: string) => {
        if (window.confirm(`Delete operation "${description}"?`)) {
            try {
                await deleteAiOp(id);
                statusContext.postError(`Operation "${description}" deleted successfully`);
                setReload(true);
            } catch (error: any) {
                statusContext.postError(`Failed to delete AI Operation: ${error.message}`);
            }
        }
    };

    const handleQueue = async (id: string, description: string, runView: boolean ) => {
        try {
            let probeId = await queueAiProbe(id);
            statusContext.postInfo(`Queued probe ${probeId} for AI Op ${id} (${description})`);
            if (runView) {
                const res = await execControl(`run ${probeId}`);
                navigate(`/probe/exec`);
            }
        } catch (error: any) {
            statusContext.postError(`Error queueing operation "${description}": ${error.message}`);
        }
    };

    const columns: ColumnDef<AiOp>[] = [
        {
            id: 'select',
            header: ({ table }) => (
            <Form.Check
                type="checkbox"
                checked={table.getIsAllRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
            />
            ),
            cell: ({ row }) => (
                <Form.Check
                    type="checkbox"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
        },
        {
            header: 'ID',
            accessorKey: 'id',
        },
        {
            header: 'Description',
            accessorKey: 'description',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <span
                        style={{ 
                            cursor: 'pointer',
                            color: theme.colors.link || theme.colors.ctext,
                            textDecoration: 'underline'
                        }}
                        onClick={() => {
                            navigate(`/ai/edit_op/${row.id}`);
                        }}>
                        {row.description}
                    </span>
                );
            }
        },
        {
            header: 'Context ID',
            accessorKey: 'context_id',
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <div className="d-flex">
                        <Button
                            size="sm"
                            className="me-2"
                            variant="outline-success"
                            onClick={() => handleQueue(row.id, row.description, true)}
                            style={{
                                backgroundColor: 'transparent',
                                borderColor: theme.colors.light,
                                color: theme.colors.light,
                            }}
                        >
                            <FontAwesomeIcon icon={faPersonRunning} />
                        </Button>
                        <Button
                            size="sm"
                            className="me-2"
                            variant="outline-success"
                            onClick={() => handleQueue(row.id, row.description, false)}
                            style={{
                                backgroundColor: 'transparent',
                                borderColor: theme.colors.success,
                                color: theme.colors.success,
                            }}
                        >
                            <FontAwesomeIcon icon={faQ} />
                        </Button>
                        <Button
                            size="sm"
                            className="me-2"
                            variant="outline-primary"
                            onClick={() => navigate(`/ai/edit_op/${row.id}`)}
                            style={{
                                backgroundColor: 'transparent',
                                borderColor: theme.colors.primary,
                                color: theme.colors.primary,
                            }}
                        >
                            <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(row.id, row.description)}
                            style={{
                                backgroundColor: 'transparent',
                                borderColor: theme.colors.danger,
                                color: theme.colors.danger
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </div>
                );
            }
        },
    ];

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        globalFilterFn: 'includesString',
        enableGlobalFilter: true,
        enableMultiRowSelection: true,
        onRowSelectionChange: setRowSelection as any,
        getRowId: (row: any) => row.id.toString(),
        state: {
            rowSelection,
            globalFilter
        }
    });

    return (
        <Container style={{ backgroundColor: theme.colors.tableBg, color: theme.colors.ctext }}>
            <div className="mb-3">
                <input
                    value={globalFilter ?? ''}
                    onChange={e => {
                        let newValue = String(e.target.value);
                        setGlobalFilter(newValue);
                        table.setGlobalFilter(newValue);
                    }}
                    className="form-control"
                    placeholder="Search operations..."
                    style={{ 
                        backgroundColor: theme.colors.bg, 
                        color: theme.colors.ctext,
                        borderColor: theme.colors.border
                    } as any}
                />
            </div>
            <BTable 
                hover 
                responsive 
                size="sm" 
                style={{ 
                    "--bs-table-bg": theme.colors.tableBg,
                    "--bs-table-color": theme.colors.ctext
                } as any}
            >
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>{headerGroup.headers.map(header => (
                        <th 
                            key={header.id} 
                            colSpan={header.colSpan}
                        >
                            <div 
                                onClick={header.column.getToggleSortingHandler()}
                                style={{ cursor: 'pointer' }}
                            >
                                {header.isPlaceholder ? null : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                                {header.column.getIsSorted() ? (
                                    <SortIcon>
                                        <FontAwesomeIcon icon={header.column.getIsSorted() === 'desc' ? faCaretDown : faCaretUp} />
                                    </SortIcon>
                                ) : null}
                            </div>
                        </th>
                    ))}</tr>
                ))}</thead>
                <tbody>
                    {table.getRowModel().rows.map(row => (
                    <tr 
                        key={row.id} 
                        style={{
                            backgroundColor: row.getIsSelected() 
                                ? (theme.colors.selectedRowBg || '#384152') 
                                : theme.colors.tableBg,
                        }}
                    >
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id} >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}</tbody>
            </BTable>
        </Container>
    );
}

const SortIcon = styled.span`
    margin-left: 5px;
`;

export default AiOpsTable;